/**
 * @file In-memory registry of {@link UploadKindAdapter} instances.
 * Initialized once by {@link initUploadManager} when {@link UploadsProvider} mounts.
 */

import type { UploadKindAdapter } from './upload-kind-adapter';

const adaptersByKind = new Map<string, UploadKindAdapter>();

/**
 * Registers upload kind adapters. Called once during upload manager initialization.
 * @param adapters - Feature adapter implementations to register.
 */
export const initUploadKindAdapters = (adapters: UploadKindAdapter[]): void => {
  adapters.forEach((adapter) => {
    adaptersByKind.set(adapter.kind, adapter);
  });
};

/**
 * Looks up the adapter for a given upload kind.
 * @param kind - Upload kind string from file or session meta.
 */
export const getUploadKindAdapter = (
  kind: string
): UploadKindAdapter | undefined => adaptersByKind.get(kind);

/**
 * Returns the human-readable feature label for a kind, or the kind string as fallback.
 * @param kind - Upload kind string.
 */
export const getUploadFeatureLabel = (kind: string): string =>
  adaptersByKind.get(kind)?.featureLabel ?? kind;

/**
 * Collects all XHR-allowed meta field names across registered adapters.
 * Used by Uppy XHRUpload `allowedMetaFields` and hydration meta extraction.
 */
export const getXhrAllowedMetaFields = (): string[] => {
  const fields = new Set<string>();
  adaptersByKind.forEach((adapter) => {
    adapter.xhrAllowedMetaFields?.forEach((field) => fields.add(field));
  });
  return Array.from(fields);
};
